import Phaser from "phaser";
import i18next from "i18next";
import { NavButton } from "../../ui/nav-button";
import { getSafeAreaInsetLeft, getSafeAreaInsetRight } from "../../utils/mobile-utils";
import { getSeasonProgress } from "../../models/season";

export type TabKey = "raider" | "workshop" | "storage" | "quests" | "season" | "empty" | "deploy";

// Note: 'empty' and 'deploy' are not shown as tab buttons in the nav
// 'empty' is intentionally excluded from TAB_ORDER so it is not reachable via swipe
// 'season' is also excluded — it's a fixed full-screen overlay, not a carousel panel,
// so it isn't part of the horizontal scroll sequence (see BaseScene.openSeasonScreen)
export const TAB_ORDER: TabKey[] = ["raider", "workshop", "storage", "quests", "deploy"];
export const NAV_BAR_HEIGHT = 80;

const seasonBtnLevelText = (seasonXp: number): string => `LVL ${getSeasonProgress(seasonXp).level}`;

// Icons used by the nav bar — must already be loaded via preloadPhaserAssets / WindowManager.preload
// raider-icon   → WindowManager.preload
// workshop-icon → WindowManager.preload
// storage-icon  → WindowManager.preload
// quests-icon   → WindowManager.preload
// equip-action  → preloadPhaserAssets
// shop-icon     → WindowManager.preload


export class BaseNav {
  private raiderBtn: NavButton;
  private workshopBtn: NavButton;
  private storageBtn: NavButton;
  private questsBtn: NavButton;
  private seasonBtn: NavButton;
  private deployBtn: NavButton;
  private bgContainer: Phaser.GameObjects.Container;

  constructor(
    scene: Phaser.Scene,
    uiLayer: Phaser.GameObjects.Container,
    onTabChange: (tab: TabKey) => void,
    onDeploy: () => void,
  ) {
    const { width, height } = scene.scale;
    const navY = height - NAV_BAR_HEIGHT - 20;
    const btnY = navY + NAV_BAR_HEIGHT / 2;

    // No background — buttons float over the scene
    this.bgContainer = scene.add.container(0, 0, []);
    this.bgContainer.setDepth(100001);
    uiLayer.add(this.bgContainer);

    // ── SEASON tab — far left, round button ───────────────────────────────────
    const SEASON_D = 128;
    const seasonX = getSafeAreaInsetLeft() + SEASON_D / 2 - 20;
    const seasonRightEdge = seasonX + SEASON_D / 2;

    // ── Button layout ────────────────────────────────────────────────────────
    // 4 regular tabs + deploy action, packed tightly. The previous width-proportional
    // spacing scaled buttons UP on wide screens with no upper bound, and its centering
    // math had an off-by-one-slot error — together they could push PLAY off the right
    // edge on narrower landscape widths (and there was no right-edge safe-area check
    // at all). This uses comfortable fixed sizes, but scales them down (never up) if
    // the space between the season button and the right safe area is ever tighter
    // than that, so the row is guaranteed to fit rather than just fitting in testing.
    const BTN_W_DEFAULT = 60;
    const ACTION_BTN_W_DEFAULT = 148;
    const GAP_DEFAULT = 20;
    const rowRightMargin = 16 + getSafeAreaInsetRight();
    const rowLeftBound = seasonRightEdge + 16;
    const availableW = width - rowLeftBound - rowRightMargin;
    const desiredRowW = 4 * BTN_W_DEFAULT + ACTION_BTN_W_DEFAULT + 4 * GAP_DEFAULT;
    const scale = Math.min(1, availableW / desiredRowW);

    const BTN_W = BTN_W_DEFAULT * scale;
    const ACTION_BTN_W = ACTION_BTN_W_DEFAULT * scale;
    const GAP = GAP_DEFAULT * scale;
    const rowW = desiredRowW * scale;
    const rowStart = rowLeftBound + (availableW - rowW) / 2; // center within the available space

    const tab1X = rowStart + BTN_W / 2;
    const tab2X = tab1X + BTN_W + GAP;
    const tab3X = tab2X + BTN_W + GAP;
    const tab4X = tab3X + BTN_W + GAP;
    const deployX = tab4X + BTN_W / 2 + GAP + ACTION_BTN_W / 2;

    const hasPremiumSeasonPass = !!(window as any).player?.data?.seasonPassOwned;
    const seasonXp = (window as any).player?.data?.seasonXp ?? 0;
    console.log("[BaseNav] seasonPassOwned:", hasPremiumSeasonPass);

    this.seasonBtn = new NavButton({
      scene,
      x: seasonX,
      y: btnY + 20,
      text: "SEASON ONE",
      secondaryText: seasonBtnLevelText(seasonXp),
      secondaryFontSizeScale: 1.5,
      icon: "star-filled",
      labelFontSize: "15px",
      style: "season",
      checkerTexture: hasPremiumSeasonPass ? "season-checkered-premium" : "season-checkered-free",
      seasonProgress: getSeasonProgress(seasonXp).progress,
      contentOffsetY: -16,
      width: SEASON_D,
      round: true,
      onClick: () => onTabChange("season"),
    });
    this.seasonBtn.setDepth(100002);
    uiLayer.add(this.seasonBtn);

    // ── RAIDER tab ────────────────────────────────────────────────────────────
    this.raiderBtn = new NavButton({
      scene,
      x: tab1X,
      y: btnY,
      text: "RAIDER",
      icon: "raider-icon",
      width: BTN_W,
      onClick: () => onTabChange("raider"),
    });
    this.raiderBtn.setDepth(100002);
    uiLayer.add(this.raiderBtn);

    // ── WORKSHOP tab ──────────────────────────────────────────────────────────
    this.workshopBtn = new NavButton({
      scene,
      x: tab2X,
      y: btnY,
      text: "WORKSHOP",
      icon: "workshop-icon",
      width: BTN_W,
      onClick: () => onTabChange("workshop"),
    });
    this.workshopBtn.setDepth(100002);
    uiLayer.add(this.workshopBtn);

    // ── STORAGE tab ───────────────────────────────────────────────────────────
    this.storageBtn = new NavButton({
      scene,
      x: tab3X,
      y: btnY,
      text: "STORAGE",
      icon: "storage-icon",
      width: BTN_W,
      onClick: () => onTabChange("storage"),
    });
    this.storageBtn.setDepth(100002);
    uiLayer.add(this.storageBtn);

    // ── QUESTS tab ────────────────────────────────────────────────────────────
    this.questsBtn = new NavButton({
      scene,
      x: tab4X,
      y: btnY,
      text: "QUESTS",
      icon: "quests-icon",
      width: BTN_W,
      onClick: () => onTabChange("quests"),
    });
    this.questsBtn.setDepth(100002);
    uiLayer.add(this.questsBtn);

    // ── DEPLOY — action button (green) ────────────────────────────────────────
    this.deployBtn = new NavButton({
      scene,
      x: deployX,
      y: btnY,
      text: i18next.t("ui:buttons.play"),
      style: "success",
      width: ACTION_BTN_W,
      onClick: onDeploy,
    });
    this.deployBtn.setDepth(100002);
    uiLayer.add(this.deployBtn);

    // Start with raider tab active
    this.raiderBtn.setTabActive(true);
  }

  hide(): void {
    this.raiderBtn.setVisible(false);
    this.workshopBtn.setVisible(false);
    this.storageBtn.setVisible(false);
    this.questsBtn.setVisible(false);
    this.seasonBtn.setVisible(false);
    this.deployBtn.setVisible(false);
  }

  show(): void {
    this.raiderBtn.setVisible(true);
    this.workshopBtn.setVisible(true);
    this.storageBtn.setVisible(true);
    this.questsBtn.setVisible(true);
    this.seasonBtn.setVisible(true);
    this.deployBtn.setVisible(true);
  }

  setSeasonPremium(owned: boolean): void {
    console.log("[BaseNav] seasonPassOwned changed:", owned);
    this.seasonBtn.setCheckerTexture(owned ? "season-checkered-premium" : "season-checkered-free");
  }

  setSeasonLevel(seasonXp: number): void {
    this.seasonBtn.setSecondaryText(seasonBtnLevelText(seasonXp));
    this.seasonBtn.setSeasonProgress(getSeasonProgress(seasonXp).progress);
  }

  setActiveTab(tab: TabKey | null): void {
    this.raiderBtn.setTabActive(tab === "raider");
    this.workshopBtn.setTabActive(tab === "workshop");
    this.storageBtn.setTabActive(tab === "storage");
    this.questsBtn.setTabActive(tab === "quests");
    this.seasonBtn.setTabActive(tab === "season");
  }

  destroy(): void {
    this.bgContainer.destroy();
    this.raiderBtn.destroy();
    this.workshopBtn.destroy();
    this.storageBtn.destroy();
    this.questsBtn.destroy();
    this.seasonBtn.destroy();
    this.deployBtn.destroy();
  }
}

